import React, { useEffect, useState } from 'react';
import { Spin, Alert, Card, Form, DatePicker, Select, Button, Input, Row, Col, Tabs } from 'antd';
import moment from 'moment';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import Analytics from './Analytics';
import ExportModal from './ExportModal';
import FilterModal from './FilterModal';
import ManualResolveModal from './ManualResolveModal';
import Layout from '../../components/Layout';
import AntTable from '../../components/CustomTable/AntTable';
import { customBaseUrl, httpGet, httpPost } from '../../store/http';
import { NotificationManager } from 'react-notifications';

const { Option } = Select;

const TransactionList = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    current: 0,
    pageSize: 10,
  });

  const fetchTransactions = async (params = {}) => {
    setLoading(true);
    try {
      const res = await httpGet(`${customBaseUrl.tranv2}/api/v1/admin/transactions?page=0&size=20&sortBy=createdAt&sortDir=DESC`);
      if (res.data.content) {
        setTransactions(res.data.content);
        setPagination({
          ...params.pagination,
          total: res.data.totalElements,
        });
      } else {
        setError(res?.message || 'Failed to fetch transactions.');
      }
    } catch (err) {
      setError('An error occurred while fetching transactions.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const [analyticsData, setAnalyticsData] = useState(null);
  const [tierAnalyticsData, setTierAnalyticsData] = useState(null);
  const [analyticsDateRange, setAnalyticsDateRange] = useState([moment().subtract(30, 'days'), moment()]);

  const fetchAnalyticsData = async (dates = analyticsDateRange) => {
    if (!dates || dates.length < 2) return;
    const [start, end] = dates;
    const startDate = start.format('YYYY-MM-DD');
    const endDate = end.format('YYYY-MM-DD');

    try {
      const res = await httpGet(`${customBaseUrl.tranv2}/api/v1/admin/statistics/system`);
      if (res.data) {
        setAnalyticsData(res.data);
      } else {
        NotificationManager.error(res?.message || 'Failed to fetch analytics data.');
      }

      const tierRes = await httpGet(`${customBaseUrl.tranv2}/api/v1/admin/statistics/tier-accounts/cumulative?startDate=${startDate}&endDate=${endDate}`);
      if (tierRes.data) {
        setTierAnalyticsData(tierRes.data);
      } else {
        NotificationManager.error(tierRes?.message || 'Failed to fetch tier analytics data.');
      }
    } catch (err) {
      console.error('Error fetching analytics data:', err);
      NotificationManager.error('An error occurred while fetching analytics data.');
    }
  };

  const exportAnalyticsToExcel = () => {
    if (!analyticsData && !tierAnalyticsData) {
      NotificationManager.warning('No analytics data available to export.');
      return;
    }

    const wb = XLSX.utils.book_new();
    const data = [];

    // System Overview
    if (analyticsData) {
      data.push(['SYSTEM TRANSACTION OVERVIEW']);
      data.push(['Metric', 'Value']);
      data.push(['Total Transactions', analyticsData.totalTransactions]);
      data.push(['Successful Transactions', analyticsData.successfulTransactions]);
      data.push(['Pending Transactions', analyticsData.pendingTransactions]);
      data.push(['Failed Transactions', analyticsData.failedTransactions]);
      data.push([]);
    }

    // Tier Analytics
    if (tierAnalyticsData) {
      const categories = [
        { key: 'personal', name: 'PERSONAL ACCOUNTS' },
        { key: 'nonCac', name: 'NON-CAC BUSINESS ACCOUNTS' },
        { key: 'cac', name: 'CAC BUSINESS ACCOUNTS' },
      ];

      categories.forEach(cat => {
        const stats = tierAnalyticsData[cat.key];
        if (stats) {
          data.push([cat.name]);
          data.push(['Tier', 'Count', 'Amount (₦)']);
          data.push(['Tier 1', stats.tier1, stats.tier1Amount]);
          data.push(['Tier 2', stats.tier2, stats.tier2Amount]);
          data.push(['Tier 3', stats.tier3, stats.tier3Amount]);
          data.push(['Tier 4', stats.tier4, stats.tier4Amount]);
          data.push(['Total', stats.total, stats.totalAmount]);
          data.push([]);
        }
      });

      data.push(['OVERALL CUMULATIVE SUMMARY']);
      data.push(['Metric', 'Value']);
      data.push(['Total Accounts', tierAnalyticsData.total]);
      data.push(['Total Amount (₦)', tierAnalyticsData.totalAmount]);
    }

    const ws = XLSX.utils.aoa_to_sheet(data);
    XLSX.utils.book_append_sheet(wb, ws, 'Analytics');

    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });

    let filename = `Analytics_${moment().format('YYYY-MM-DD_HH-mm')}.xlsx`;
    if (analyticsDateRange && analyticsDateRange.length === 2) {
      const start = analyticsDateRange[0].format('YYYY-MM-DD');
      const end = analyticsDateRange[1].format('YYYY-MM-DD');
      filename = `Analytics_${start}_to_${end}.xlsx`;
    }

    saveAs(new Blob([wbout], { type: 'application/octet-stream' }), filename);
  };

  useEffect(() => {
    fetchTransactions();
    fetchAnalyticsData();
  }, []);

  const columns = [
    { title: 'Transaction ID', dataIndex: 'transactionId', key: 'transactionId' },
    { title: 'Reference ID', dataIndex: 'reference', key: 'refrence' },
    { title: 'Transaction Type', dataIndex: 'transactionType', key: 'transactionType' },
    { title: 'Amount', dataIndex: 'amount', key: 'amount' },
    { title: 'From Account', dataIndex: 'fromAccountNumber', key: 'fromAccountNumber' },
    { title: 'To Account', dataIndex: 'toAccountNumber', key: 'toAccountNumber' },
    { title: 'Sender Name', dataIndex: 'payerName', key: 'payerName' },
    { title: 'Receiver Name', dataIndex: 'receiverName', key: 'receiverName' },
    { title: 'Status', dataIndex: 'status', key: 'status' },
    { title: 'Created At', dataIndex: 'createdAt', key: 'createdAt' },
    { title: 'Updated At', dataIndex: 'updatedAt', key: 'updatedAt' },
    {
      title: 'Action',
      key: 'action',
      render: (text, record) => (
        record.status !== 'SUCCESS' && (
          <Button size="small" type="primary" onClick={() => showManualResolveModal(record)}>
            Manual Resolve
          </Button>
        )
      ),
    },
  ];

  const [isExportModalVisible, setIsExportModalVisible] = useState(false);
  const [isManualResolveModalVisible, setIsManualResolveModalVisible] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [manualResolveLoading, setManualResolveLoading] = useState(false);

  const showManualResolveModal = (record) => {
    setSelectedTransaction(record);
    setIsManualResolveModalVisible(true);
  };

  const handleManualResolveCancel = () => {
    setIsManualResolveModalVisible(false);
    setSelectedTransaction(null);
  };

  const handleManualResolve = async (values) => {
    if (!selectedTransaction) return;
    const { status, comment } = values;
    const { reference } = selectedTransaction;
    const url = `${customBaseUrl.tranv2}/api/v1/admin/maker-checker/manual-resolve?reference=${reference}&status=${status}&comment=${comment}`;

    setManualResolveLoading(true);
    try {
      const res = await httpPost(url);
      if (res.status === true || res.message === 'Success' || res.data) {
        NotificationManager.success('Transaction resolved successfully.');
        setIsManualResolveModalVisible(false);
        fetchTransactions();
      } else {
        NotificationManager.error(res?.message || 'Failed to resolve transaction.');
      }
    } catch (error) {
      console.error('Error resolving transaction:', error);
      NotificationManager.error('An error occurred while resolving transaction.');
    } finally {
      setManualResolveLoading(false);
    }
  };

  const showExportModal = () => {
    setIsExportModalVisible(true);
  };

  const handleExportModalCancel = () => {
    setIsExportModalVisible(false);
  };

  const handleExport = async (values) => {
    const { startDate, endDate, status, type } = values;
    const formattedStartDate = startDate.format('YYYY-MM-DD HH:mm:ss');
    const formattedEndDate = endDate.format('YYYY-MM-DD HH:mm:ss');
    const url = `${customBaseUrl.tranv2}/api/v1/admin/transactions/export?startDate=${formattedStartDate}&endDate=${formattedEndDate}&status=${status}&type=${type}`;
    try {
      const res = await httpGet(url);
      const blob = new Blob([res], { type: 'text/csv' });
      saveAs(blob, 'transactions.csv');
      setIsExportModalVisible(false);
    } catch (error) {
      console.error('Error exporting transactions:', error);
      NotificationManager.error('Failed to export transactions.');
    }
  };

  // return (
  //   <Layout>
  //     <Card title="All Transactions">
  //       {analyticsData && <Analytics data={analyticsData} />}
  const [isFilterModalVisible, setIsFilterModalVisible] = useState(false);

  const showFilterModal = () => {
    setIsFilterModalVisible(true);
  };

  const handleFilterModalCancel = () => {
    setIsFilterModalVisible(false);
  };

  const handleFilter = async (values) => {
    let url = `${customBaseUrl.tranv2}/api/v1/admin/transactions`;
    const { transactionId, userId, reference, startDate, endDate } = values;

    if (transactionId) {
      url = `${customBaseUrl.tranv2}/api/v1/admin/transactions/${transactionId}`;
    } else if (userId) {
      url = `${customBaseUrl.tranv2}/api/v1/admin/transactions/user/${userId}?page=0&size=20`;
    } else if (reference) {
      url = `${customBaseUrl.tranv2}/api/v1/admin/transactions/reference/${reference}`;
    } else if (startDate && endDate) {
      const formattedStartDate = startDate.format('YYYY-MM-DD HH:mm:ss');
      const formattedEndDate = endDate.format('YYYY-MM-DD HH:mm:ss');
      url = `${customBaseUrl.tranv2}/api/v1/admin/transactions/date-range?startDate=${formattedStartDate}&endDate=${formattedEndDate}&page=0&size=20`;
    }

    setLoading(true);
    try {
      const res = await httpGet(url);
      if (res.data.content) {
        setTransactions(res.data.content);
        setPagination({
          ...pagination,
          total: res.data.totalElements,
        });
      } else if (res.data) {
        setTransactions([res.data]);
        setPagination({
          ...pagination,
          total: 1,
        });
      } else {
        setError(res?.message || 'Failed to fetch transactions.');
      }
      setIsFilterModalVisible(false);
    } catch (err) {
      setError('An error occurred while fetching transactions.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const clearFilter = () => {
    fetchTransactions();
  };

  return (
    <Layout>
      <Card
        title="Transactions Management"
        extra={
          <div style={{ display: 'flex', gap: '10px' }}>
            <Button type="primary" onClick={showFilterModal}>
              Filter Transactions
            </Button>
            <Button type="primary" onClick={showExportModal}>
              Export Transactions
            </Button>
            <Button
              type="primary"
              onClick={exportAnalyticsToExcel}
              disabled={!analyticsData && !tierAnalyticsData}
            >
              Download Analytics
            </Button>
          </div>
        }
      >
        <Tabs defaultActiveKey="1">
          <Tabs.TabPane tab="Analytics" key="1">
            <div style={{ marginBottom: '20px', marginTop: '10px' }}>
              <Row justify="space-between" align="middle">
                <Col>
                  <span style={{ marginRight: '10px', fontWeight: 'bold' }}>Analytics Period:</span>
                  <DatePicker.RangePicker
                    value={analyticsDateRange}
                    onChange={(dates) => setAnalyticsDateRange(dates)}
                    format="YYYY-MM-DD"
                  />
                  <Button
                    type="primary"
                    onClick={() => fetchAnalyticsData()}
                    style={{ marginLeft: '10px' }}
                  >
                    Search
                  </Button>
                </Col>
              </Row>
            </div>
            <Analytics data={analyticsData} tierData={tierAnalyticsData} />
          </Tabs.TabPane>
          <Tabs.TabPane tab="Transactions" key="2">
            <div style={{ marginTop: '15px' }}>
              <FilterModal
                visible={isFilterModalVisible}
                onCancel={handleFilterModalCancel}
                onFilter={handleFilter}
              />
              <ExportModal
                visible={isExportModalVisible}
                onCancel={handleExportModalCancel}
                onExport={handleExport}
              />
              <ManualResolveModal
                visible={isManualResolveModalVisible}
                onCancel={handleManualResolveCancel}
                onResolve={handleManualResolve}
                loading={manualResolveLoading}
              />
              {loading ? (
                <Spin tip="Loading transactions..." />
              ) : error ? (
                <Alert message="Error" description={error} type="error" showIcon />
              ) : (
                <AntTable
                  pagination={pagination}
                  users={transactions}
                  loading={loading}
                  handleGetNext={fetchTransactions}
                  columns={columns}
                />
              )}
            </div>
          </Tabs.TabPane>
        </Tabs>
      </Card>
    </Layout >
  );
};

export default TransactionList;